import {memoize} from 'lodash';
import type {PaginationSettings, ReactableColumnDef} from '@deviceinsight/ng-ui-components';
import {ReactableWithClientSidePagination} from '@deviceinsight/ng-ui-components';
import {InfoWidgetItem, isInfoWidgetDatapoint, TableItem} from './types';
import {DEFAULT_ROWS, getDatapointDisplayValue} from './config/ConfigUtils';
import {getDisplayValue, translateCustomTokens, NO_VALUE_DISPLAY} from './InfoWidgetUtils';
import {TFunction} from '@deviceinsight/ng-ui-scale-lib/api';

type PaginatedProps = {
	items: Array<InfoWidgetItem>;
	values: Array<TableItem>;
	rows: number;
	filterable: boolean;
	t: TFunction;
	className: string;
};
type ValueItemProps = {
	tableItem: TableItem;
};
type ValueLabelProps = {
	displayValue: string;
};

const initPaginationSettings = (rowsPerPage: number): PaginationSettings => {
	return {
		page: 0,
		size: rowsPerPage,
		sort: {
			property: 'name',
			direction: 'ASC'
		}
	};
};

const ValueLabel = ({displayValue}: ValueLabelProps) => <label title={displayValue}>{displayValue}</label>;

const ValueDisplay = ({tableItem}: ValueItemProps) => {
	const {item, value} = tableItem;

	if (isInfoWidgetDatapoint(item)) {
		const {datapoint, decimalPoints} = item;
		const displayValue = getDatapointDisplayValue(datapoint, decimalPoints, value);
		return <ValueLabel displayValue={displayValue} />;
	} else {
		const displayValue = getDisplayValue(value);
		return <ValueLabel displayValue={displayValue} />;
	}
};

const getLabel = (tableItem: TableItem, t: TFunction): string => {
	const {item} = tableItem;

	if (isInfoWidgetDatapoint(item)) {
		const {datapoint, label} = item;
		const datapointName = datapoint ? datapoint.name : NO_VALUE_DISPLAY;
		return translateCustomTokens(label ? label : datapointName, t);
	} else {
		const {propertyKey, label} = item;
		const propertyName = propertyKey ? propertyKey : NO_VALUE_DISPLAY;
		return translateCustomTokens(label ? label : propertyName, t);
	}
};

const getColumnDefs = memoize((filterable: boolean, t: TFunction): Array<ReactableColumnDef<TableItem>> => {
	return [
		{
			name: 'label',
			label: {
				id: 'dashboard.widget.info.column.label',
				defaultValue: 'Label'
			},
			width: '50%',
			sortable: false,
			filterable: filterable
				? {
						filterType: 'text',
						filterFn: (tableItem: TableItem, value: string) =>
							getLabel(tableItem, t).toLowerCase().indexOf(value.toLowerCase()) >= 0
				  }
				: undefined,
			render: (tableItem) => <span>{getLabel(tableItem, t)}</span>
		},
		{
			name: 'value',
			label: {
				id: 'dashboard.widget.info.column.value',
				defaultValue: 'Value'
			},
			width: '50%',
			sortable: false,
			align: 'right',
			render: (tableItem) => <ValueDisplay tableItem={tableItem} />
		}
	];
});

const InfoWidgetPaginated = ({values, rows, filterable, t, className}: PaginatedProps) => {
	const columDefs = getColumnDefs(filterable, t);
	const paginationSettings = initPaginationSettings(rows || DEFAULT_ROWS);
	return (
		<div className={className}>
			<ReactableWithClientSidePagination
				columnDefs={columDefs}
				uniqueItemKey={(tableItem) => tableItem.key}
				initialPaginationSettings={paginationSettings}
				items={values}
				isLoading={false}
			/>
		</div>
	);
};

export default InfoWidgetPaginated;
